import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AuthenticatedAppWrapper from "./components/Permissions/AuthenticatedAppWrapper";
import DevAuthWrapper from "./components/Permissions/DevAuthWrapper";
import Layout from "./components/Layout";
import PermissionDebug from "./components/Permissions/PermissionDebug";
import BPAsPage from "./pages/BPAs";
import DirectoriesPage from "./pages/Directories";
import HomePage from "./pages/HomePage";
import NotFoundPage from "./pages/NotFound";
import PreviewPage from "./pages/Preview";
import SwaggerPage from "./pages/Swagger";
import { ROUTES } from "./routes.js";
import EditionsPage from "./pages/Editions";
import IndustrySectorsPage from "./pages/IndustrySectors";
import RegionsPage from "./pages/Regions";
import RegionCountriesPage from "./pages/RegionCountries";
import MiscellaneousPage from "./pages/Miscellaneous/index.js";
import FeaturesPage from "./pages/Features";
import PackagesLayout from "./pages/Packages/PackagesLayout";
import PackageList from "./pages/Packages/PackageList";
import PackageDetail from "./pages/Packages/PackageDetail";
import UserVerificationPage from "./pages/UserVerification";
import SubscriptionsPage from "./pages/Subscriptions";
import SubscriptionsLayout from "./pages/Subscriptions/SubscriptionsLayout";
import SubscriptionDetail from "./pages/Subscriptions/SubscriptionDetail";
import { AuthProvider } from "./contexts/AuthContext.js";
import { FeatureFlagsProvider } from "./contexts/FeatureFlagsContext.js";
import FeatureFlagsGate from "./components/Permissions/FeatureFlagsGate";
import RequireFlag from "./components/Permissions/RequireFlag";
import RequirePermission from "./components/Permissions/RequirePermission";
import MasterCompaniesPage from "./pages/AddressBook/MasterCompanies";
import PendingCompaniesPage from "./pages/AddressBook/PendingCompanies";
import CompaniesPage from "./pages/AddressBook/Companies";
import CompanyDetail from "./pages/AddressBook/Companies/CompanyDetail";
import MergeWizardPage from "./pages/AddressBook/Companies/MergeWizardPage";
import DemergeWizardPage from "./pages/AddressBook/Companies/DemergeWizardPage";
import ContactsPage from "./pages/AddressBook/Contacts";
import ContactDetail from "./pages/AddressBook/Contacts/ContactDetail";
import ContactMergeWizard from "./pages/AddressBook/Contacts/ContactMergeWizard";
import ContactDemergeWizard from "./pages/AddressBook/Contacts/ContactDemergeWizard";
import ContactSplitWizard from "./pages/AddressBook/Contacts/ContactSplitWizard";
import OfficesPage from "./pages/AddressBook/Offices";
import OfficeDetail from "./pages/AddressBook/Offices/OfficeDetail";
import OfficeMergeWizard from "./pages/AddressBook/Offices/OfficeMergeWizard";
import OfficeDemergeWizard from "./pages/AddressBook/Offices/OfficeDemergeWizard";
import CompanyOfficeNew from "./pages/AddressBook/Companies/CompanyOfficeNew";
import { DataMartsLayout, DataMartList, DataMartDetail } from "./pages/DataMarts";
import AwardsPage from "./pages/Awards/index.js";
import AwardFormPage from "./pages/Awards/AwardFormPage.js";
import { DashboardEdit, DashboardList } from "./pages/DataProducts/index.js";
import PowerlistSeriesListPage from "./pages/Powerlist/SeriesListPage";
import PowerlistSeriesEditionsPage from "./pages/Powerlist/SeriesEditionsPage";
import PowerlistEditionDetailPage from "./pages/Powerlist/EditionDetailPage";
import PowerlistEditionPreviewPage from "./pages/Powerlist/EditionPreviewPage";
import PowerlistEntryEditorPage from "./pages/Powerlist/EntryEditorPage";
import PowerlistQuestionBankPage from "./pages/Powerlist/QuestionBankPage";
import {
  AUDIT_READ_PERMISSION,
  AWARDS_READ_PERMISSION,
  AWARDS_WRITE_PERMISSION,
  DATA_PRODUCTS_READ_PERMISSION,
  POWERLIST_READ_PERMISSION,
  POWERLIST_WRITE_PERMISSION,
  TAXONOMY_READ_PERMISSION,
} from "./constants/permissions.js";
import {
  SubmissionsList,
  RefereeContactList,
  DocumentParsingPage,
  ParsingJobDetail,
  RollingSubmissionsList,
  RollingSubmissionDetail,
  AiSchemasPage,
  AiMarkdownTemplatesPage,
  AiParseRunsPage,
} from "./pages/Submissions/index.js";
import SyncersPage from "./pages/Developer/Syncers";
import QueueMonitorPage from "./pages/Developer/QueueMonitor/index.js";
import ParseMonitoringPage from "./pages/Developer/ParseMonitoring/index.js";
import ServicesPage from "./pages/Developer/Services/index.js";
import FeatureFlagsPage from "./pages/Developer/FeatureFlags/index.js";
import LoadTestAccountsPage from "./pages/Developer/LoadTestAccounts/index.js";
import NotificationsPage from "./pages/Developer/Notifications/index.js";
import PipelineBrowserPage from "./pages/Developer/PipelineBrowser/index.js";
import { MoveToSetRunProvider } from "./pages/Developer/PipelineBrowser/BatchUpdates/MoveToSetRunProvider.js";
import { MoveBpaRunProvider } from "./pages/Developer/PipelineBrowser/MoveBpa/MoveBpaRunProvider.js";
import ParseMonitoringDetail from "./pages/Developer/ParseMonitoring/ParseMonitoringDetail.js";
import EditorialTransformPage from "./pages/Developer/EditorialTransform/index.js";
import DeveloperUsersPage from "./pages/Developer/Users/index.js";
import AccountRemediationPage from "./pages/Developer/AccountRemediation/index.js";
import CompanySlugDriftPage from "./pages/Developer/CompanySlugDrift/index.js";
import CrmWebhookPage from "./pages/Developer/CrmWebhook/index.js";
import WebsiteRelabelPage from "./pages/Developer/WebsiteRelabel/index.js";
import OfficeIdentityBackfillPage from "./pages/Developer/OfficeIdentityBackfill/index.js";
import OfficeSlugBackfillPage from "./pages/Developer/OfficeSlugBackfill/index.js";
import ContactSlugBackfillPage from "./pages/Developer/ContactSlugBackfill/index.js";
import ContactMatchBackfillPage from "./pages/Developer/ContactMatchBackfill/index.js";
import NameTranslationsImportPage from "./pages/Developer/NameTranslationsImport/index.js";
import ResearchDataImportPage from "./pages/Developer/ResearchDataImport/index.js";
import AwardsImportPage from "./pages/Developer/AwardsImport/index.js";
import FirmLogoCopyPage from "./pages/Developer/FirmLogoCopy/index.js";
import ContactRoleEndDateBackfillPage from "./pages/Developer/ContactRoleEndDateBackfill/index.js";
import SurveyTestimonialReplayPage from "./pages/Developer/SurveyTestimonialReplay/index.js";
import AddressBookRenameReplayPage from "./pages/Developer/AddressBookRenameReplay/index.js";
import AuditLogPage from "./pages/Research/AuditLog/index.js";
import LegacyAuditLogRedirect from "./pages/Research/AuditLog/LegacyAuditLogRedirect";
import ResearchDbPage from "./pages/Developer/ResearchWpMappings/index.js";
import DbManagerPage from "./pages/Developer/DbManager/index.js";
import SlotDetailPage from "./pages/Developer/DbManager/SlotDetailPage.js";
import RedisBreakdownPage from "./pages/Developer/DbManager/RedisBreakdownPage.js";
import WpSyncerPage from "./pages/L500Data/WpSyncer/index.js";
import ImageCachePage from "./pages/L500Data/ImageCache/index.js";
import PromoteImpactPage from "./pages/L500Data/PromoteImpact/index.js";
import IntelligenceSyncerPage from "./pages/L500Data/IntelligenceSyncer/index.js";
import BulkExternalRefsPage from "./pages/L500Data/BulkExternalRefs/index.js";
import BulkSubscriptionUpdatePage from "./pages/BulkSubscriptionUpdate/index.js";
import {
  CrmPackageMappingsLayout,
  CrmPackageMappingList,
  CrmPackageMappingDetail,
} from "./pages/CrmPackageMappings";
import {
  CrmWebhookLogsLayout,
  CrmWebhookLogList,
  CrmWebhookLogDetail,
} from "./pages/CrmWebhookLogs";
import ContentInboxPage from "./pages/Research/ContentInbox";
import ContentInboxDetail from "./pages/Research/ContentInbox/ContentInboxDetail";
import TestimonialsPage from "./pages/Research/Testimonials";
import TestimonialDetail from "./pages/Research/Testimonials/TestimonialDetail";
import CommentariesPage from "./pages/Research/Commentaries/index.js";
import CreateCommentaryPage from "./pages/Research/Commentaries/CreateCommentary.js";
import EditCommentaryPage from "./pages/Research/Commentaries/EditCommentary.js";
import { CmsBpaMergeHubPage } from "./pages/Research/Cms/CmsBpaMergeHubPage";
import StatisticsPage from "./pages/Research/Statistics";
import {
  ResearchCmsHome,
  CmsPublicationPage,
  CmsBpaPage,
  CmsBpaImportPage,
  CmsBpaImportReviewPage,
  CmsBpaSplitHubPage,
  CmsOrgPage,
  CmsIndividualPage,
  CmsRankingTablePage,
  CmsManagePage,
  CmsCreateBpaPage,
  CmsEditBpaPage,
} from "./pages/Research/Cms";
import CmsBpaImportRoute from "./pages/Research/Cms/CmsBpaImportRoute";
import FirmDashboard from "./pages/Fulfilment/FirmDashboard";
import RegionPublicationsPage from "./pages/RegionPublications/index.js";
import SurveyManagementPage from "./pages/Surveys/Management";
import SurveyDetailsPage from "./pages/Surveys/Details";
import QuestionLibraryPage from "./pages/Surveys/QuestionLibrary";
import RandomQuestionsPage from "./pages/Surveys/RandomQuestions";
import SurveyResponsesPage from "./pages/Surveys/Responses";
import SurveyResponseDetailPage from "./pages/Surveys/Responses/ResponseDetail";
import CopyTemplatesPage from "./pages/MarketingToolkit/CopyTemplates";
import MarketingToolkitTermsPage from "./pages/MarketingToolkit/Terms";
import MarketingToolkitDownloadAuditPage from "./pages/MarketingToolkit/DownloadAudit";
import MarketingToolkitEmbargoTextsPage from "./pages/MarketingToolkit/EmbargoTexts";
import MarketingToolkitUserGuidePage from "./pages/MarketingToolkit/UserGuide";
import MarketingToolkitFirmDiagnosticsPage from "./pages/MarketingToolkit/FirmDiagnostics";
import MarketingToolkitRegionMappingPage from "./pages/MarketingToolkit/RegionMapping";
import MailingGroupsPage from "./pages/MailingGroups";
import MailingGroupDetail from "./pages/MailingGroups/MailingGroupDetail";
import MailingGroupView from "./pages/MailingGroups/MailingGroupView";
import CampaignDetail from "./pages/MailingGroups/CampaignDetail";
import SegmentDetail from "./pages/MailingGroups/SegmentDetail";
import MachineAccountsLayout from "./pages/MachineAccounts/MachineAccountsLayout";
import MachineAccountList from "./pages/MachineAccounts/MachineAccountList";
import MachineAccountDetail from "./pages/MachineAccounts/MachineAccountDetail";
import GatewayAllowlistLayout from "./pages/GatewayAllowlist/GatewayAllowlistLayout";
import GatewayAllowlistList from "./pages/GatewayAllowlist/GatewayAllowlistList";
import UsersLayout from "./pages/UserManagement/Users/UsersLayout";
import UserList from "./pages/UserManagement/Users/UserList";
import UserDetail from "./pages/UserManagement/Users/UserDetail";
import OrganisationsLayout from "./pages/UserManagement/Organisations/OrganisationsLayout";
import OrganisationList from "./pages/UserManagement/Organisations/OrganisationList";
import OrganisationNew from "./pages/UserManagement/Organisations/OrganisationNew";
import OrganisationDetail from "./pages/UserManagement/Organisations/OrganisationDetail";
import RolesPage from "./pages/UserManagement/Roles/RolesPage";
import CustomerSupportPage from "./pages/UserManagement/CustomerSupport/CustomerSupportPage";
import WebsiteUserDetailPage from "./pages/UserManagement/CustomerSupport/WebsiteUserDetailPage";
import WebsiteUserRedirect from "./pages/UserManagement/CustomerSupport/WebsiteUserRedirect";
import MyAccountPage from "./pages/MyAccount";
import ProfilesDashboard from "./pages/Profiles/ProfilesDashboard";
import FirmInspector from "./pages/Profiles/FirmInspector";
import FirmDetail from "./pages/Profiles/FirmDetail";
import FirmTabsList from "./pages/Profiles/FirmTabs";
import FirmTabsKindRedirect from "./pages/Profiles/FirmTabs/KindRedirect";
import FirmTabDetail from "./pages/Profiles/FirmTabs/Detail";
import FirmTabNew from "./pages/Profiles/FirmTabs/New";
import ArticlesList from "./pages/Profiles/Articles";
import ArticleDetail from "./pages/Profiles/Articles/Detail";
import ArticleNew from "./pages/Profiles/Articles/New";
import LawyerProfilesList from "./pages/Profiles/LawyerProfiles";
import LawyerProfileDetail from "./pages/Profiles/LawyerProfiles/Detail";
import LawyerProfileNew from "./pages/Profiles/LawyerProfiles/New";
import FirmProfilesList from "./pages/Profiles/FirmProfiles";
import FirmProfileDetail from "./pages/Profiles/FirmProfiles/Detail";
import FirmProfileNew from "./pages/Profiles/FirmProfiles/New";
import TeamPagesList from "./pages/Profiles/TeamPages";
import TeamPageDetail from "./pages/Profiles/TeamPages/Detail";
import TeamPageNew from "./pages/Profiles/TeamPages/New";
import { UserGuidePage } from "./pages/Profiles/UserGuide";
import AuditPage from "./pages/Profiles/Audit";
import ContentMovesPage from "./pages/Profiles/ContentMoves";
import PagesListPage from "./pages/CMS/PagesListPage";
import HomepagePage from "./pages/CMS/HomepagePage";
import PageContentPage from "./pages/CMS/PageContentPage";
import PageNewPage from "./pages/CMS/PageNewPage";
import PageDetailPage from "./pages/CMS/PageDetailPage";
import MenusPage from "./pages/CMS/MenusPage";
import EventsListPage from "./pages/Events/EventsListPage";
import HeroBannerPage from "./pages/Events/HeroBannerPage";
import { OptOutsPage } from "./pages/RefereeManagement";
import RegistrationGatePage from "./pages/RegistrationGate/RegistrationGate";
import MailingTemplatesPage from "./pages/Surveys/MailingTemplates";
import PlatformAuditPage from "./pages/Audit";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { shouldRetryQuery } from "./api/serviceUnavailable";
import { publicRuntimeConfig } from "./config/publicRuntimeConfig";

const isAuthDisabled = publicRuntimeConfig.authDisabled;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
      staleTime: 30_000,
    },
  },
});

function App() {
  const routes = (
    <AuthProvider>
      <FeatureFlagsProvider>
        <FeatureFlagsGate>
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path={ROUTES.taxonomy.regions} element={<RegionsPage />} />
              <Route path={ROUTES.taxonomy.regionCountries} element={<RegionCountriesPage />} />
              <Route path={ROUTES.taxonomy.bpas} element={<BPAsPage />} />
              <Route path={ROUTES.taxonomy.directories} element={<DirectoriesPage />} />
              <Route path={ROUTES.taxonomy.editions} element={<EditionsPage />} />
              <Route
                path={ROUTES.taxonomy.industrySectors}
                element={
                  <RequirePermission permission={TAXONOMY_READ_PERMISSION} redirectTo="/">
                    <IndustrySectorsPage />
                  </RequirePermission>
                }
              />
              <Route path={ROUTES.taxonomy.miscellaneous} element={<MiscellaneousPage />} />
              <Route path={ROUTES.fulfilment.features} element={<FeaturesPage />} />
              <Route path={ROUTES.fulfilment.packages} element={<PackagesLayout />}>
                <Route index element={<PackageList />} />
                <Route path=":id" element={<PackageDetail />} />
              </Route>
              <Route
                path="/users/company-users"
                element={<Navigate to="/data-marts/account" replace />}
              />
              <Route
                path="/data-marts/company-users"
                element={<Navigate to="/data-marts/account" replace />}
              />
              <Route path={ROUTES.users.userVerification} element={<UserVerificationPage />} />
              <Route path={ROUTES.fulfilment.subscriptions} element={<SubscriptionsLayout />}>
                <Route index element={<SubscriptionsPage />} />
                <Route path=":id" element={<SubscriptionDetail />} />
              </Route>
              <Route path="/fulfilment/firms/:firmId" element={<FirmDashboard />} />
              <Route path={ROUTES.users.regionPublications} element={<RegionPublicationsPage />} />
              <Route
                path={ROUTES.fulfilment.crmPackageMappings}
                element={<CrmPackageMappingsLayout />}
              >
                <Route index element={<CrmPackageMappingList />} />
                <Route path=":id" element={<CrmPackageMappingDetail />} />
              </Route>
              <Route path={ROUTES.fulfilment.crmWebhookLogs} element={<CrmWebhookLogsLayout />}>
                <Route index element={<CrmWebhookLogList />} />
                <Route path=":id" element={<CrmWebhookLogDetail />} />
              </Route>
              <Route
                path={ROUTES.fulfilment.bulkSubscriptionUpdate}
                element={<BulkSubscriptionUpdatePage />}
              />
              <Route path={ROUTES.websiteUsers.companies} element={<MasterCompaniesPage />} />
              <Route
                path={ROUTES.websiteUsers.pendingCompanies}
                element={<PendingCompaniesPage />}
              />
              <Route path={ROUTES.addressBook.companies} element={<CompaniesPage />} />
              <Route path={`${ROUTES.addressBook.companies}/:id`} element={<CompanyDetail />} />
              <Route
                path="/address-book/companies/:id/merge"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.companies}>
                    <MergeWizardPage />
                  </RequireFlag>
                }
              />
              <Route
                path="/address-book/companies/:id/demerge"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.companies}>
                    <DemergeWizardPage />
                  </RequireFlag>
                }
              />
              <Route path={ROUTES.addressBook.contacts} element={<ContactsPage />} />
              <Route
                path="/address-book/contacts/:id/merge"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.contacts}>
                    <ContactMergeWizard />
                  </RequireFlag>
                }
              />
              <Route
                path="/address-book/contacts/:id/demerge"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.contacts}>
                    <ContactDemergeWizard />
                  </RequireFlag>
                }
              />
              <Route
                path="/address-book/contacts/:id/split"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.contacts}>
                    <ContactSplitWizard />
                  </RequireFlag>
                }
              />
              <Route path={`${ROUTES.addressBook.contacts}/:id`} element={<ContactDetail />} />
              <Route path={ROUTES.addressBook.offices} element={<OfficesPage />} />
              <Route
                path="/address-book/offices/:id/merge"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.offices}>
                    <OfficeMergeWizard />
                  </RequireFlag>
                }
              />
              <Route
                path="/address-book/offices/:id/demerge"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.offices}>
                    <OfficeDemergeWizard />
                  </RequireFlag>
                }
              />
              <Route path={`${ROUTES.addressBook.offices}/:id`} element={<OfficeDetail />} />
              <Route
                path="/address-book/companies/:companyId/offices/new"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.companies}>
                    <CompanyOfficeNew />
                  </RequireFlag>
                }
              />
              <Route
                path="/address-book/companies/:companyId/contacts/new"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.companies}>
                    <ContactDetail />
                  </RequireFlag>
                }
              />
              <Route
                path="/address-book/offices/:officeId/contacts/new"
                element={
                  <RequireFlag flag="address-book-live" redirectTo={ROUTES.addressBook.offices}>
                    <ContactDetail />
                  </RequireFlag>
                }
              />
              <Route path={ROUTES.dataMarts.root} element={<DataMartsLayout />}>
                <Route index element={<DataMartList />} />
              </Route>
              <Route path={`${ROUTES.dataMarts.root}/:dataMartId`} element={<DataMartDetail />} />
              <Route
                path={ROUTES.awards.list}
                element={
                  <RequirePermission permission={AWARDS_READ_PERMISSION} redirectTo="/">
                    <AwardsPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.awards.new}
                element={
                  <RequirePermission permission={AWARDS_WRITE_PERMISSION} redirectTo="/">
                    <AwardFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.awards.edit(":id")}
                element={
                  <RequirePermission permission={AWARDS_WRITE_PERMISSION} redirectTo="/">
                    <AwardFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.dataProducts.omni}
                element={
                  <RequirePermission permission={DATA_PRODUCTS_READ_PERMISSION} redirectTo="/">
                    <DashboardList kind="omni" />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.dataProducts.omniNew}
                element={
                  <RequirePermission permission={DATA_PRODUCTS_READ_PERMISSION} redirectTo="/">
                    <DashboardEdit kind="omni" />
                  </RequirePermission>
                }
              />
              <Route
                path={`${ROUTES.dataProducts.omni}/:id`}
                element={
                  <RequirePermission permission={DATA_PRODUCTS_READ_PERMISSION} redirectTo="/">
                    <DashboardEdit kind="omni" />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.dataProducts.historicalData}
                element={
                  <RequirePermission permission={DATA_PRODUCTS_READ_PERMISSION} redirectTo="/">
                    <DashboardList kind="tableau" />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.dataProducts.historicalDataNew}
                element={
                  <RequirePermission permission={DATA_PRODUCTS_READ_PERMISSION} redirectTo="/">
                    <DashboardEdit kind="tableau" />
                  </RequirePermission>
                }
              />
              <Route
                path={`${ROUTES.dataProducts.historicalData}/:id`}
                element={
                  <RequirePermission permission={DATA_PRODUCTS_READ_PERMISSION} redirectTo="/">
                    <DashboardEdit kind="tableau" />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.powerlist.series}
                element={
                  <RequirePermission permission={POWERLIST_READ_PERMISSION} redirectTo="/">
                    <PowerlistSeriesListPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.powerlist.seriesDetail(":seriesId")}
                element={
                  <RequirePermission permission={POWERLIST_READ_PERMISSION} redirectTo="/">
                    <PowerlistSeriesEditionsPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.powerlist.edition(":editionId")}
                element={
                  <RequirePermission permission={POWERLIST_READ_PERMISSION} redirectTo="/">
                    <PowerlistEditionDetailPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.powerlist.preview(":editionId")}
                element={
                  <RequirePermission permission={POWERLIST_READ_PERMISSION} redirectTo="/">
                    <PowerlistEditionPreviewPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.powerlist.newEntry(":editionId")}
                element={
                  <RequirePermission permission={POWERLIST_WRITE_PERMISSION} redirectTo="/">
                    <PowerlistEntryEditorPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.powerlist.entry(":editionId", ":entryId")}
                element={
                  <RequirePermission permission={POWERLIST_READ_PERMISSION} redirectTo="/">
                    <PowerlistEntryEditorPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.powerlist.questions}
                element={
                  <RequirePermission permission={POWERLIST_READ_PERMISSION} redirectTo="/">
                    <PowerlistQuestionBankPage />
                  </RequirePermission>
                }
              />
              <Route path={ROUTES.submissions.list} element={<SubmissionsList />} />
              <Route path={ROUTES.submissions.referees} element={<RefereeContactList />} />
              <Route
                path={`${ROUTES.submissions.documentParsing}/jobs/:id`}
                element={<ParsingJobDetail />}
              />
              <Route path={ROUTES.submissions.documentParsing} element={<DocumentParsingPage />} />
              <Route path={ROUTES.submissions.aiSchemas} element={<AiSchemasPage />} />
              <Route
                path={ROUTES.submissions.aiMarkdownTemplates}
                element={<AiMarkdownTemplatesPage />}
              />
              <Route path={ROUTES.submissions.aiParseRuns} element={<AiParseRunsPage />} />
              <Route
                path={ROUTES.submissions.rollingSubmissions}
                element={<RollingSubmissionsList />}
              />
              <Route
                path="/submissions/rolling-submissions/:id"
                element={<RollingSubmissionDetail />}
              />
              <Route path={ROUTES.developer.swagger} element={<SwaggerPage />} />
              <Route path={ROUTES.developer.syncers} element={<SyncersPage />} />
              <Route path={ROUTES.developer.queueMonitor} element={<QueueMonitorPage />} />
              <Route path={ROUTES.developer.parseMonitoring} element={<ParseMonitoringPage />} />
              <Route
                path={ROUTES.developer.editorialTransform}
                element={<EditorialTransformPage />}
              />
              <Route path={ROUTES.developer.services} element={<ServicesPage />} />
              <Route path={ROUTES.developer.featureFlags} element={<FeatureFlagsPage />} />
              <Route
                path={ROUTES.developer.loadTestAccounts}
                element={
                  <RequirePermission permission="platform.read" redirectTo="/">
                    <LoadTestAccountsPage />
                  </RequirePermission>
                }
              />
              <Route
                path={ROUTES.developer.notifications}
                element={<Navigate to={ROUTES.developer.notificationsCapabilities} replace />}
              />
              <Route
                path={ROUTES.developer.notificationsCapabilities}
                element={<NotificationsPage />}
              />
              <Route
                path={ROUTES.developer.notificationsEventLog}
                element={<NotificationsPage />}
              />
              <Route
                path={ROUTES.audit.list}
                element={
                  <RequirePermission permission="audit.read" redirectTo="/">
                    <PlatformAuditPage />
                  </RequirePermission>
                }
              />
              <Route path={ROUTES.developer.pipelineBrowser} element={<PipelineBrowserPage />} />
              <Route
                path={`${ROUTES.developer.parseMonitoring}/jobs/:id`}
                element={<ParseMonitoringDetail />}
              />
              <Route path={ROUTES.developer.users} element={<DeveloperUsersPage />} />
              <Route
                path={ROUTES.developer.accountRemediation}
                element={<AccountRemediationPage />}
              />
              <Route path={ROUTES.developer.crmWebhook} element={<CrmWebhookPage />} />
              <Route path={ROUTES.developer.companySlugDrift} element={<CompanySlugDriftPage />} />
              <Route path={ROUTES.developer.websiteRelabel} element={<WebsiteRelabelPage />} />
              <Route
                path={ROUTES.developer.officeIdentityBackfill}
                element={<OfficeIdentityBackfillPage />}
              />
              <Route
                path={ROUTES.developer.officeSlugBackfill}
                element={<OfficeSlugBackfillPage />}
              />
              <Route
                path={ROUTES.developer.contactSlugBackfill}
                element={<ContactSlugBackfillPage />}
              />
              <Route
                path={ROUTES.developer.contactMatchBackfill}
                element={<ContactMatchBackfillPage />}
              />
              <Route
                path={ROUTES.developer.nameTranslationsImport}
                element={<NameTranslationsImportPage />}
              />
              <Route
                path={ROUTES.developer.researchDataImport}
                element={<ResearchDataImportPage />}
              />
              <Route
                path={ROUTES.developer.contactRoleEndDateBackfill}
                element={<ContactRoleEndDateBackfillPage />}
              />
              <Route
                path={ROUTES.developer.surveyTestimonialReplay}
                element={<SurveyTestimonialReplayPage />}
              />
              <Route
                path={ROUTES.developer.addressBookRenameReplay}
                element={<AddressBookRenameReplayPage />}
              />
              <Route path={ROUTES.developer.researchDb} element={<ResearchDbPage />} />
              <Route path={ROUTES.developer.awardsImport} element={<AwardsImportPage />} />
              <Route path={ROUTES.developer.firmLogoCopy} element={<FirmLogoCopyPage />} />
              <Route path={ROUTES.l500Data.redisLoading} element={<DbManagerPage />} />
              <Route
                path={ROUTES.l500Data.redisLoadingKeyspace(":keyspace")}
                element={<SlotDetailPage />}
              />
              <Route path={ROUTES.l500Data.redisBreakdown} element={<RedisBreakdownPage />} />
              <Route path={ROUTES.l500Data.wpSyncer} element={<WpSyncerPage />} />
              <Route path={ROUTES.l500Data.wpSyncerImageCache} element={<ImageCachePage />} />
              <Route path={ROUTES.l500Data.promoteImpact} element={<PromoteImpactPage />} />
              <Route
                path={ROUTES.l500Data.intelligenceSyncer}
                element={<IntelligenceSyncerPage />}
              />
              <Route path={ROUTES.l500Data.bulkExternalRefs} element={<BulkExternalRefsPage />} />
              <Route path={ROUTES.marketingToolkit.copyTemplates} element={<CopyTemplatesPage />} />
              <Route path={ROUTES.marketingToolkit.terms} element={<MarketingToolkitTermsPage />} />
              <Route
                path={ROUTES.marketingToolkit.downloadAudit}
                element={<MarketingToolkitDownloadAuditPage />}
              />
              <Route
                path={ROUTES.marketingToolkit.embargoTexts}
                element={<MarketingToolkitEmbargoTextsPage />}
              />
              <Route
                path={ROUTES.marketingToolkit.userGuide}
                element={<MarketingToolkitUserGuidePage />}
              />
              <Route
                path={ROUTES.marketingToolkit.firmDiagnostics}
                element={<MarketingToolkitFirmDiagnosticsPage />}
              />
              <Route
                path={ROUTES.marketingToolkit.regionMapping}
                element={<MarketingToolkitRegionMappingPage />}
              />
              <Route path={ROUTES.surveys.management} element={<SurveyManagementPage />} />
              <Route path={ROUTES.surveys.campaigns} element={<MailingGroupsPage />} />
              <Route
                path={ROUTES.surveys.campaignDetail(":publicationId", ":edition")}
                element={<MailingGroupDetail />}
              />
              <Route
                path={ROUTES.surveys.campaignGroupDetail(":publicationId", ":edition", ":groupId")}
                element={<MailingGroupView />}
              />
              <Route
                path={ROUTES.surveys.campaignDetailPage(
                  ":publicationId",
                  ":edition",
                  ":groupId",
                  ":campaignId",
                )}
                element={<CampaignDetail />}
              />
              <Route
                path={ROUTES.surveys.campaignSendDetail(
                  ":publicationId",
                  ":edition",
                  ":groupId",
                  ":campaignId",
                  ":segmentId",
                )}
                element={<SegmentDetail />}
              />
              <Route path={ROUTES.surveys.detail(":surveyId")} element={<SurveyDetailsPage />} />
              <Route path={ROUTES.surveys.questionLibrary} element={<QuestionLibraryPage />} />
              <Route path={ROUTES.surveys.randomQuestions} element={<RandomQuestionsPage />} />
              <Route path={ROUTES.surveys.responses} element={<SurveyResponsesPage />} />
              <Route
                path={ROUTES.surveys.responseDetail(":responseId")}
                element={<SurveyResponseDetailPage />}
              />
              <Route path={ROUTES.research.contentInbox} element={<ContentInboxPage />} />
              <Route
                path={`${ROUTES.research.contentInbox}/:id`}
                element={<ContentInboxDetail />}
              />
              <Route path={ROUTES.research.testimonials} element={<TestimonialsPage />} />
              <Route path={`${ROUTES.research.testimonials}/:id`} element={<TestimonialDetail />} />
              <Route
                path={ROUTES.research.auditLog}
                element={
                  <RequirePermission permission={AUDIT_READ_PERMISSION} redirectTo="/">
                    <AuditLogPage />
                  </RequirePermission>
                }
              />
              <Route path="/developer/audit-log" element={<LegacyAuditLogRedirect />} />
              <Route path={ROUTES.research.commentaries} element={<CommentariesPage />} />
              <Route
                path={`${ROUTES.research.commentaries}/:id`}
                element={<EditCommentaryPage />}
              />
              <Route path={ROUTES.research.createCommentary} element={<CreateCommentaryPage />} />
              <Route path={ROUTES.research.statistics} element={<StatisticsPage />} />
              <Route
                path={ROUTES.research.cms}
                element={
                  <RequirePermission permission="staff.research.read" redirectTo="/">
                    <ResearchCmsHome />
                  </RequirePermission>
                }
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId`}
                element={
                  <RequirePermission permission="staff.research.read" redirectTo="/">
                    <CmsPublicationPage />
                  </RequirePermission>
                }
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId/bpa/:bpaId/import/review`}
                element={
                  <CmsBpaImportRoute>
                    <CmsBpaImportReviewPage />
                  </CmsBpaImportRoute>
                }
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId/bpa/:bpaId/import`}
                element={
                  <CmsBpaImportRoute>
                    <CmsBpaImportPage />
                  </CmsBpaImportRoute>
                }
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId/bpa/:bpaId/split`}
                element={<CmsBpaSplitHubPage />}
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId/bpa/:bpaId/merge`}
                element={<CmsBpaMergeHubPage />}
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId/bpa/:bpaId`}
                element={
                  <RequirePermission permission="staff.research.read" redirectTo="/">
                    <CmsBpaPage />
                  </RequirePermission>
                }
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId/bpa/:bpaId/org/:companyUuid`}
                element={
                  <RequirePermission permission="staff.research.read" redirectTo="/">
                    <CmsOrgPage />
                  </RequirePermission>
                }
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId/bpa/:bpaId/individual/:contactUuid`}
                element={
                  <RequirePermission permission="staff.research.read" redirectTo="/">
                    <CmsIndividualPage />
                  </RequirePermission>
                }
              />
              <Route
                path={`${ROUTES.research.cms}/:editionId/bpa/:bpaId/ranking-table/:rankingTableId`}
                element={
                  <RequirePermission permission="staff.research.read" redirectTo="/">
                    <CmsRankingTablePage />
                  </RequirePermission>
                }
              />
              <Route
                path="/research/publications/:id/manage"
                element={
                  <RequirePermission permission="staff.research.read" redirectTo="/">
                    <CmsManagePage />
                  </RequirePermission>
                }
              />
              <Route
                path="/research/publications/:id/create-bpa"
                element={
                  <RequirePermission permission="staff.research.create" redirectTo="/">
                    <CmsCreateBpaPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/research/publications/:id/edit-bpa/:bpaId"
                element={
                  <RequirePermission permission="staff.research.edit" redirectTo="/">
                    <CmsEditBpaPage />
                  </RequirePermission>
                }
              />
              <Route element={<UsersLayout />}>
                <Route
                  path={ROUTES.users.accountServices}
                  element={<UserList context="account" />}
                />
                <Route path={ROUTES.users.staff} element={<UserList context="staff" />} />
                <Route path="/users/detail/:id" element={<UserDetail />} />
              </Route>
              <Route path="/users/organisations" element={<OrganisationsLayout />}>
                <Route index element={<OrganisationList />} />
                <Route path="new" element={<OrganisationNew />} />
                <Route path=":id" element={<OrganisationDetail />} />
              </Route>
              <Route path="/users/machine-accounts" element={<MachineAccountsLayout />}>
                <Route index element={<MachineAccountList />} />
                <Route path=":id" element={<MachineAccountDetail />} />
              </Route>
              <Route path={ROUTES.gatewayAllowlist.list} element={<GatewayAllowlistLayout />}>
                <Route index element={<GatewayAllowlistList />} />
              </Route>
              <Route path="/users/roles" element={<RolesPage />} />
              <Route path={ROUTES.users.website}>
                <Route index element={<CustomerSupportPage />} />
                <Route path=":userId" element={<WebsiteUserDetailPage />} />
              </Route>
              <Route
                path="/website-users"
                element={<Navigate to={ROUTES.users.website} replace />}
              />
              <Route path="/website-users/:userId" element={<WebsiteUserRedirect />} />
              <Route path={ROUTES.account.me} element={<MyAccountPage />} />
              <Route path={ROUTES.registrationGate} element={<RegistrationGatePage />} />
              <Route
                path="/email-gate"
                element={<Navigate to={ROUTES.registrationGate} replace />}
              />
              <Route path={ROUTES.surveys.optOuts} element={<OptOutsPage />} />
              <Route path={ROUTES.surveys.templates} element={<MailingTemplatesPage />} />
              <Route
                path={ROUTES.cms.homepage}
                element={
                  <RequirePermission permission="cms.read" redirectTo="/">
                    <HomepagePage />
                  </RequirePermission>
                }
              />
              <Route path={ROUTES.cms.pages} element={<PagesListPage />} />
              <Route path={ROUTES.cms.pageContent} element={<PageContentPage />} />
              <Route path={ROUTES.cms.pageNew} element={<PageNewPage />} />
              <Route path="/cms/pages/:id" element={<PageDetailPage />} />
              <Route path={ROUTES.cms.menus} element={<MenusPage />} />
              <Route path={ROUTES.events.list} element={<EventsListPage />} />
              <Route path={ROUTES.events.heroBanner} element={<HeroBannerPage />} />
              <Route path={ROUTES.profiles.dashboard} element={<ProfilesDashboard />} />
              <Route path={ROUTES.profiles.firms} element={<FirmInspector />} />
              <Route path="/profiles/firms/:companyId" element={<FirmDetail />} />
              <Route path={ROUTES.profiles.firmTabs} element={<FirmTabsList />} />
              <Route path="/profiles/firm-tabs/:kind/new" element={<FirmTabNew />} />
              <Route path="/profiles/firm-tabs/:kind/:id" element={<FirmTabDetail />} />
              <Route path="/profiles/firm-tabs/:kind" element={<FirmTabsKindRedirect />} />
              <Route path={ROUTES.profiles.articles} element={<ArticlesList />} />
              <Route path={ROUTES.profiles.articleNew} element={<ArticleNew />} />
              <Route path="/profiles/articles/:id" element={<ArticleDetail />} />
              <Route path={ROUTES.profiles.lawyerProfiles} element={<LawyerProfilesList />} />
              <Route path={ROUTES.profiles.lawyerProfileNew} element={<LawyerProfileNew />} />
              <Route path="/profiles/lawyer-profiles/:id" element={<LawyerProfileDetail />} />
              <Route path={ROUTES.profiles.firmProfiles} element={<FirmProfilesList />} />
              <Route path={ROUTES.profiles.firmProfileNew} element={<FirmProfileNew />} />
              <Route path="/profiles/firm-profiles/:id" element={<FirmProfileDetail />} />
              <Route path={ROUTES.profiles.teamPages} element={<TeamPagesList />} />
              <Route path={ROUTES.profiles.teamPageNew} element={<TeamPageNew />} />
              <Route path="/profiles/team-pages/:id" element={<TeamPageDetail />} />
              <Route path={ROUTES.profiles.userGuide} element={<UserGuidePage />} />
              <Route path={ROUTES.profiles.audit} element={<AuditPage />} />
              <Route path={ROUTES.profiles.contentMoves} element={<ContentMovesPage />} />
              {process.env.NODE_ENV === "development" && (
                <Route path={ROUTES.taxonomy.preview} element={<PreviewPage />} />
              )}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Layout>
          <PermissionDebug />
        </FeatureFlagsGate>
      </FeatureFlagsProvider>
    </AuthProvider>
  );

  const withQueryClient = (children: ReactNode) => (
    <QueryClientProvider client={queryClient}>
      <MoveToSetRunProvider>
        <MoveBpaRunProvider>{children}</MoveBpaRunProvider>
      </MoveToSetRunProvider>
    </QueryClientProvider>
  );

  if (isAuthDisabled) {
    return withQueryClient(<DevAuthWrapper>{routes}</DevAuthWrapper>);
  }

  return withQueryClient(<AuthenticatedAppWrapper>{routes}</AuthenticatedAppWrapper>);
}

export default App;
